/**
 * "Change vs Control, over time": the leading version's cumulative change on
 * the selected goal, with its likely range as two dashed edges around it and a
 * line at no change. The range narrows as visitors come in; a range that stays
 * clear of the line is a lead that holds. No area fill (Paper "Charts · Card
 * pattern"); the line is ink until the goal can be called, then the view's one
 * orange.
 *
 * Built from the primary request's daily series (`{id}_sessions`,
 * `{id}_conversionRate`), which only the launch goal has. Daily visitors sum
 * a returning visitor once per day, so the last point can sit a little off
 * the headline figure.
 *
 * Band: a normal approximation on the log of the rate ratio (delta method,
 * Beta / Gamma posterior means; count variance × dispersion), cheap enough
 * for every day. The last point uses the exact 20,000-draw range from
 * bayes.ts so it matches the versions table.
 */

import { useMemo } from "react"
import { CartesianGrid, ComposedChart, Line, ReferenceLine, XAxis, YAxis } from "recharts"
import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart"
import { compareProportions, compareRates, formatRange } from "@/lib/experiments/bayes"
import { CALL_CHANCE } from "@/lib/experiments/goalMetric"

type Point = { date: string; change: number | null; band: [number, number] | null; chance: number | null }

/** Most points the chart computes a range for; longer tests are thinned evenly (last day kept). */
const MAX_POINTS = 60

const fmtDate = (value: string) => {
  const d = new Date(`${value}T00:00:00`)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}
const fmtPct = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(Math.round(v))}%`

/** Round ticks that always include 0, e.g. −50%, 0%, +50%, +100%. */
export function liftTicks(lo: number, hi: number): number[] {
  const span = Math.max(1, hi - lo)
  const step = [5, 10, 25, 50, 100, 200].find((s) => span / s <= 4) ?? 500
  const out: number[] = []
  for (let v = Math.floor(lo / step) * step; v <= Math.ceil(hi / step) * step; v += step) out.push(v)
  return out
}

/** 95% band of θ_B / θ_A − 1 in percent, from a normal approximation on log(θ_B / θ_A). */
function approxBand(
  p: { cs: number; ce: number; vs: number; ve: number },
  metric: "proportion" | "count",
  dispersion: number,
): [number, number] {
  const aA = 1 + p.ce
  const aB = 1 + p.ve
  let mean: number
  let variance: number
  if (metric === "count") {
    const phi = Math.max(1, dispersion)
    mean = Math.log(aB / p.vs) - Math.log(aA / p.cs)
    variance = phi / aA + phi / aB
  } else {
    const nA = p.cs + 2
    const nB = p.vs + 2
    mean = Math.log(aB / nB) - Math.log(aA / nA)
    variance = (1 - aA / nA) / aA + (1 - aB / nB) / aB
  }
  const sd = Math.sqrt(variance)
  return [(Math.exp(mean - 1.96 * sd) - 1) * 100, (Math.exp(mean + 1.96 * sd) - 1) * 100]
}

export function liftSeries(
  timeSeries: Array<Record<string, unknown>>,
  controlId: string,
  variantId: string,
  metric: "proportion" | "count",
  dispersion = 1,
): Point[] {
  const acc = { cs: 0, ce: 0, vs: 0, ve: 0 }
  const cumulative = timeSeries.map((row) => {
    const cs = Number(row[`${controlId}_sessions`] ?? 0) || 0
    const vs = Number(row[`${variantId}_sessions`] ?? 0) || 0
    acc.cs += cs
    acc.vs += vs
    acc.ce += ((Number(row[`${controlId}_conversionRate`] ?? 0) || 0) / 100) * cs
    acc.ve += ((Number(row[`${variantId}_conversionRate`] ?? 0) || 0) / 100) * vs
    return { date: String(row.date), ...acc }
  })
  const step = Math.max(1, Math.ceil(cumulative.length / MAX_POINTS))
  const picked = cumulative.filter((_, i) => i % step === 0 || i === cumulative.length - 1)
  return picked.map((p, i) => {
    if (p.cs <= 0 || p.vs <= 0 || p.ce <= 0) return { date: p.date, change: null, band: null, chance: null }
    const change = ((p.ve / p.vs - p.ce / p.cs) / (p.ce / p.cs)) * 100
    if (i === picked.length - 1) {
      const b =
        metric === "count"
          ? compareRates(Math.round(p.ce), p.cs, Math.round(p.ve), p.vs, dispersion)
          : compareProportions(Math.round(p.ce), p.cs, Math.round(p.ve), p.vs)
      return { date: p.date, change, band: b.range ? [b.range.lower, b.range.upper] : null, chance: b.chance }
    }
    return { date: p.date, change, band: approxBand(p, metric, dispersion), chance: null }
  })
}

export function LiftChart({
  timeSeries,
  controlId,
  variantId,
  variantLabel,
  metric,
  dispersion,
  callable = false,
}: {
  timeSeries: Array<Record<string, unknown>>
  controlId: string
  variantId: string
  variantLabel: string
  metric: "proportion" | "count"
  dispersion?: number
  /** The goal can be called: only then is the line orange. */
  callable?: boolean
}) {
  const data = useMemo(() => liftSeries(timeSeries, controlId, variantId, metric, dispersion), [timeSeries, controlId, variantId, metric, dispersion])
  const last = [...data].reverse().find((p) => p.change != null)
  const strong = callable && last?.chance != null && last.chance >= CALL_CHANCE
  const color = strong ? "var(--primary)" : "var(--foreground)"
  const config: ChartConfig = { change: { label: variantLabel, color } }
  const rows = useMemo(() => data.map((p) => ({ ...p, lower: p.band?.[0] ?? null, upper: p.band?.[1] ?? null })), [data])
  // Clip runaway early bands so the recent days stay readable.
  const values = data.flatMap((p) => [p.change, ...(p.band ?? [])]).filter((v): v is number => v != null)
  const clip = Math.max(50, Math.abs(last?.change ?? 0) * 3)
  const lo = Math.max(-clip, Math.min(0, ...values))
  const hi = Math.min(clip, Math.max(0, ...values))
  const ticks = liftTicks(lo, hi)

  if (data.filter((p) => p.change != null).length < 2) return null

  return (
    <ChartContainer config={config} className="aspect-auto h-[200px] w-full">
      <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke="var(--chart-grid)" strokeDasharray="1 3" strokeLinecap="round" />
        <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={10} minTickGap={48} tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={48}
          ticks={ticks}
          domain={[ticks[0], ticks[ticks.length - 1]]}
          allowDataOverflow
          tickFormatter={(v: number) => fmtPct(Number(v))}
          tick={{ fontSize: 11 }}
        />
        <ReferenceLine y={0} stroke="var(--ring)" />
        <ChartTooltip
          cursor={{ stroke: "currentColor", strokeOpacity: 0.12 }}
          content={({ active, payload }) => {
            const p = active ? (payload?.[0]?.payload as Point | undefined) : undefined
            if (!p || p.change == null) return null
            return (
              <div className="rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-sm">
                <div className="text-muted-foreground">{fmtDate(p.date)}</div>
                <div className="font-medium tabular-nums text-foreground">{fmtPct(p.change)}</div>
                {p.band && <div className="tabular-nums text-muted-foreground">likely {formatRange({ lower: p.band[0], upper: p.band[1] })}</div>}
              </div>
            )
          }}
        />
        <Line dataKey="upper" type="monotone" stroke="var(--ring)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} activeDot={false} isAnimationActive={false} connectNulls />
        <Line dataKey="lower" type="monotone" stroke="var(--ring)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} activeDot={false} isAnimationActive={false} connectNulls />
        <Line dataKey="change" type="monotone" stroke={color} strokeWidth={2.2} dot={false} activeDot={{ r: 4, fill: color, strokeWidth: 0 }} isAnimationActive={false} connectNulls />
      </ComposedChart>
    </ChartContainer>
  )
}
